import React, { useState, useEffect } from 'react';
import { PhoneOutlined, EnvironmentOutlined, MailOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { contactService } from '../lib/contactService';
import { UAE_EMIRATES } from '../types/ContactMessage'; 

export default function ContactForm() {
  const [formData, setFormData] = useState({
    yourName: '',
    emirate: '',
    email: '',
    phoneNo: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.yourName.trim()) newErrors.yourName = 'Please enter your name';
    else if (formData.yourName.trim().length < 2)
      newErrors.yourName = 'Name must be at least 2 characters';

    if (!formData.emirate.trim()) newErrors.emirate = 'Please select an emirate';
    else if (!UAE_EMIRATES.includes(formData.emirate))
      newErrors.emirate = 'Please select a valid UAE emirate';

    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email';

    if (!formData.phoneNo.trim()) newErrors.phoneNo = 'Please enter your phone number';
    else if (!/^[0-9+\-\s()]{8,20}$/.test(formData.phoneNo))
      newErrors.phoneNo = 'Please enter a valid phone number';

    if (!formData.message.trim()) newErrors.message = 'Please enter your message';
    else if (formData.message.trim().length < 10)
      newErrors.message = 'Message must be at least 10 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Prepare data for Firebase
      const contactData = {
        name: formData.yourName.trim(),
        emirate: formData.emirate,
        email: formData.email.trim().toLowerCase(),
        phoneNumber: formData.phoneNo.trim(),
        message: formData.message.trim()
      };

      // Submit to Firebase
      const messageId = await contactService.submitContactMessage(contactData);
      
      console.log('Message submitted successfully with ID:', messageId);
      
      // Show success popup
      setShowPopup(true);
      
      // Reset form
      setFormData({
        yourName: '',
        emirate: '',
        email: '',
        phoneNo: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrors({ submit: error.message || 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '20px',
        paddingLeft: windowWidth < 768 ? '20px' : '200px',
        paddingRight: windowWidth < 768 ? '20px' : '200px',
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          background: '#4a1a5e',
          borderRadius: '16px',
          padding: windowWidth < 768 ? '25px' : '40px',
          width: '100%',
          height: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflowY: 'auto',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'white', fontSize: '32px', fontWeight: '600', margin: '0 0 12px 0' }}>
            Let's Connect
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: 0 }}>
            We'll get back to you right away
          </p>
        </div>

        {/* Form Description */}
        <div style={{ textAlign: 'center', marginBottom: '25px', color: 'white', fontSize: '15px' }}>
          Share a few details to help us assist you better.
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Your Name & Emirate */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: 'white', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Your Name</label>
              <input
                type="text"
                name="yourName"
                value={formData.yourName}
                onChange={handleChange}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `1px solid ${errors.yourName ? '#ff6b6b' : 'rgba(255,255,255,0.3)'}`,
                  color: 'white',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {errors.yourName && <div style={{ color: '#ffb4b4', fontSize: '12px', marginTop: '4px' }}>{errors.yourName}</div>}
            </div>

            <div>
              <label style={{ color: 'white', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Emirate</label>
              <select
                name="emirate"
                value={formData.emirate}
                onChange={handleChange}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `1px solid ${errors.emirate ? '#ff6b6b' : 'rgba(255,255,255,0.3)'}`,
                  color: formData.emirate ? 'white' : 'rgba(255,255,255,0.6)',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="">Select Emirate</option>
                {UAE_EMIRATES.map(emirate => (
                  <option key={emirate} value={emirate} style={{ color: '#333' }}>
                    {emirate}
                  </option>
                ))}
              </select>
              {errors.emirate && <div style={{ color: '#ffb4b4', fontSize: '12px', marginTop: '4px' }}>{errors.emirate}</div>}
            </div>
          </div>

          {/* Email & Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: 'white', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `1px solid ${errors.email ? '#ff6b6b' : 'rgba(255,255,255,0.3)'}`,
                  color: 'white',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {errors.email && <div style={{ color: '#ffb4b4', fontSize: '12px', marginTop: '4px' }}>{errors.email}</div>}
            </div>

            <div>
              <label style={{ color: 'white', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Phone No</label>
              <input
                type="tel"
                name="phoneNo"
                value={formData.phoneNo}
                onChange={handleChange}
                placeholder="+971 XX XXX XXXX"
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `1px solid ${errors.phoneNo ? '#ff6b6b' : 'rgba(255,255,255,0.3)'}`,
                  color: 'white',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {errors.phoneNo && <div style={{ color: '#ffb4b4', fontSize: '12px', marginTop: '4px' }}>{errors.phoneNo}</div>}
            </div>
          </div>

          {/* Message */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'white', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Leave us a message...</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              placeholder="Please provide details about your inquiry..."
              style={{
                width: '100%',
                background: 'transparent',
                border: `1px solid ${errors.message ? '#ff6b6b' : 'rgba(255,255,255,0.3)'}`,
                color: 'white',
                borderRadius: '4px',
                padding: '10px 12px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            {errors.message && <div style={{ color: '#ffb4b4', fontSize: '12px', marginTop: '4px' }}>{errors.message}</div>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div style={{ color: '#ffb4b4', fontSize: '14px', textAlign: 'center', marginBottom: '15px' }}>
              {errors.submit}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'rgba(255,255,255,0.8)' : 'white',
              color: '#4a1a5e',
              border: 'none',
              height: '48px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </form>

        {/* Popup Modal */}
        {showPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}
          >
            <div
              style={{
                background: '#4a1a5e',
                padding: '40px',
                borderRadius: '16px',
                textAlign: 'center',
                color: 'white',
                width: windowWidth < 768 ? '80%' : '400px'
              }}
            >
              <CheckOutlined style={{ fontSize: '48px', color: 'white', marginBottom: '20px' }} />
              <h2 style={{ marginBottom: '10px' }}>Request Submitted Successfully!</h2>
              <p style={{ marginBottom: '20px' }}>Our team will contact you shortly.</p>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'white',
                  color: '#4a1a5e',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Go to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}